import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { QrCode, CheckCircle, Clock, NavigationArrow, MapPin, ArrowsClockwise, Lightning, FileX, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";

export function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    passengerApi.get("/me/tickets")
      .then(({ data }) => setTickets(data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="majhe-my-tickets">
      <MajheHeader title="My Tickets" onBack={() => navigate("/majhe")}/>
      <div className="px-5">
        {loading && [0, 1, 2].map((i) => <div key={i} className="majhe-skel h-24 mb-2"/>)}
        {!loading && tickets.length === 0 && (
          <div className="text-center py-12">
            <FileX size={42} className="text-[#5B2EE5] mx-auto"/>
            <div className="text-[#18142A] font-bold mt-3">No tickets yet</div>
            <div className="text-[12px] text-[#7B7390] mt-1">Book your first bus from the Book tab</div>
            <button onClick={() => navigate("/majhe/book")} className="majhe-btn mt-4">Book Now</button>
          </div>
        )}
        {tickets.map((t) => (
          <Link key={t.id} to={`/majhe/tickets/${t.ticket_no}`} data-testid="ticket-list-row"
            className="bg-white rounded-2xl border border-[#EDE8FA] p-4 mb-2.5 flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
              t.status === "validated" ? "bg-[#2D1170] text-white" : "bg-[#EDE5FF] text-[#5B2EE5]"
            }`}>
              {t.status === "validated" ? <CheckCircle size={20} weight="fill"/> : <QrCode size={20} weight="bold"/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-[#18142A] truncate">{t.route_name}</div>
              <div className="text-[11px] text-[#7B7390] truncate">{t.from_stop} → {t.to_stop}</div>
              <div className="text-[10px] text-[#94889F] mt-0.5">
                {new Date(t.issued_at).toLocaleDateString()} · ₹{t.total_paid}
              </div>
            </div>
            <span className={`majhe-pill ${t.status === "validated" ? "majhe-pill--green" : "majhe-pill--violet"}`}>
              {t.status}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function TicketView() {
  const { ticketNo } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [eta, setEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);

  const load = async () => {
    try {
      const tres = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/tickets/${ticketNo}`).then(r => r.json());
      setTicket(tres);
      if (tres?.route_id && tres?.status !== "validated") {
        const { data: eres } = await passengerApi.get(`/eta?route_id=${tres.route_id}`);
        setEta(eres);
      } else {
        setEta(null);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [ticketNo]);

  // Auto-refresh every 8s to catch ETM/admin validation in real-time
  useEffect(() => {
    if (!ticket || ticket.status === "validated") return;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [ticket?.status]);

  const dummyValidate = async () => {
    if (!ticket || ticket.status === "validated") return;
    setValidating(true);
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/tickets/${ticket.ticket_no}/validate`,
        { method: "POST" }
      ).then((r) => r.json());
      if (resp?.already_validated) {
        toast.warning("Ticket was already validated");
      } else {
        toast.success("Ticket validated · QR is now inactive");
      }
      load();
    } catch (e) {
      toast.error("Validation failed");
    } finally { setValidating(false); }
  };

  if (loading) return (
    <div data-testid="majhe-ticket-view">
      <MajheHeader title="Ticket" onBack={() => navigate("/majhe")}/>
      <div className="px-5"><div className="majhe-skel h-96 mt-2"/></div>
    </div>
  );

  if (!ticket) return (
    <div data-testid="majhe-ticket-view">
      <MajheHeader title="Ticket" onBack={() => navigate("/majhe")}/>
      <div className="px-5 text-center py-10 text-[#7B7390]">Ticket not found</div>
    </div>
  );

  const validated = ticket.status === "validated";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=4&data=${encodeURIComponent(ticket.qr_payload)}`;

  return (
    <div data-testid="majhe-ticket-view">
      <MajheHeader title="QR Bus Ticket" onBack={() => navigate("/majhe")}/>

      <div className="px-5 pt-1 pb-6">
        <div className="majhe-ticket">
          <div className="majhe-ticket__head">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider opacity-90">
              <QrCode size={14} weight="bold"/> Ticket No
            </div>
            <div className="text-xl font-extrabold mt-0.5 tracking-wide">{ticket.ticket_no}</div>
            <div className="text-sm mt-1 opacity-90">{ticket.route_name}</div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="majhe-card-btn">{ticket.passengers} passenger{ticket.passengers > 1 ? "s" : ""}</span>
              <span className="majhe-card-btn">₹{ticket.total_paid}</span>
              <span className="majhe-pill"
                style={{ background: validated ? "#DC2626" : "#F59E0B", color: "white" }}>
                {validated ? "Used / Validated" : "Active"}
              </span>
            </div>
          </div>

          <div className="majhe-ticket__qr">
            {validated ? (
              <div className="relative w-56 h-56 rounded-xl border-4 border-red-500 bg-red-50 flex items-center justify-center overflow-hidden"
                data-testid="ticket-validated-watermark"
                role="img" aria-label="Ticket validated. QR code is no longer scannable">
                {/* Faint USED background watermark (behind content) */}
                <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
                  style={{ transform: "rotate(-18deg)", color: "rgba(220,38,38,0.06)", fontSize: 76, fontWeight: 900, letterSpacing: "0.25em" }}>USED</span>
                <div className="relative text-center px-3 z-10">
                  <CheckCircle size={56} weight="fill" className="text-red-500 mx-auto" aria-hidden="true"/>
                  <div className="text-red-600 font-black text-2xl tracking-[0.2em] mt-1">VALIDATED</div>
                  <div className="text-red-500 text-[11px] mt-2 font-semibold">QR no longer scannable</div>
                  {ticket.validated_at && (
                    <div className="text-red-400 text-[10px] mt-2">{new Date(ticket.validated_at).toLocaleString()}</div>
                  )}
                </div>
              </div>
            ) : (
              <img src={qrSrc} alt={`QR code for ticket ${ticket.ticket_no}, route ${ticket.route_name}, from ${ticket.from_stop} to ${ticket.to_stop}, ${ticket.passengers} passenger${ticket.passengers > 1 ? "s" : ""}, fare ${ticket.total_paid} rupees. Show this to the conductor.`}
                data-testid="ticket-qr-img"
                className="w-56 h-56 rounded-xl border border-[#EDE8FA] bg-white"/>
            )}
          </div>

          {!validated && (
            <div className="px-5 pb-4 text-center text-[11px] text-[#7B7390] font-mono break-all">
              {ticket.qr_payload}
            </div>
          )}

          <div className="px-5 pb-5 grid grid-cols-2 gap-3">
            <DetailField label="From" value={ticket.from_stop} icon={<NavigationArrow size={14} weight="fill"/>}/>
            <DetailField label="To" value={ticket.to_stop} icon={<MapPin size={14} weight="fill"/>}/>
            <DetailField label="Passenger" value={ticket.passenger_name}/>
            <DetailField label="Mobile" value={`+91 ${ticket.passenger_mobile}`}/>
            <DetailField label="Booked At" value={new Date(ticket.issued_at).toLocaleString()}/>
            <DetailField label="Valid Until" value={new Date(ticket.valid_until).toLocaleString()}/>
            {validated && <DetailField label="Validated At" value={new Date(ticket.validated_at).toLocaleString()}/>}
            {validated && ticket.validated_via && <DetailField label="Validated Via" value={ticket.validated_via}/>}
          </div>
        </div>

        {/* Live ETA / tracking — hide once validated */}
        {eta && !validated && (
          <div className="bg-white rounded-2xl border border-[#EDE8FA] p-4 mt-4" data-testid="ticket-eta-block">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#7B7390] font-bold">Bus Status</div>
                <div className="text-lg font-extrabold text-[#18142A]">Arriving in {eta.eta_min} min</div>
              </div>
              <button onClick={load}
                type="button"
                aria-label="Refresh bus arrival status"
                className="w-9 h-9 rounded-lg bg-[#F5F2FF] text-[#5B2EE5] flex items-center justify-center" data-testid="eta-refresh">
                <ArrowsClockwise size={16} weight="bold" aria-hidden="true"/>
              </button>
            </div>
            <div className="flex items-center gap-2 mb-3 text-[11px]">
              <span className="majhe-pill majhe-pill--violet">{eta.registration_number || eta.fleet_id}</span>
              <span className="majhe-pill majhe-pill--blue">{eta.vehicle_type}</span>
              <span className="majhe-pill majhe-pill--amber">{eta.occupancy}</span>
            </div>
            <Timeline milestones={eta.milestones || []}/>
          </div>
        )}

        {/* Validate (demo / dummy) — disappears once validated */}
        {!validated && (
          <div className="mt-4 space-y-2">
            <button onClick={dummyValidate} disabled={validating} data-testid="customer-validate-btn"
              type="button"
              aria-label={validating ? "Validating ticket, please wait" : "Validate ticket as demo scan. Marks the ticket used and disables the QR code"}
              className="majhe-btn" style={{ background: "#2D1170" }}>
              <CheckCircle size={18} weight="bold" aria-hidden="true"/>
              {validating ? "Validating…" : "Validate Ticket (Demo Scan)"}
            </button>
            <div className="text-[10.5px] text-center text-[#7B7390] leading-relaxed">
              In production the conductor will scan this QR on their ETM machine. <b>Tap above for a demo scan.</b><br/>
              Validation will also reflect instantly if scanned from admin/ETM.
            </div>
          </div>
        )}

        {validated && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3.5 text-center"
            data-testid="ticket-validated-banner"
            role="status" aria-live="polite">
            <div className="text-red-700 font-bold text-sm">✓ Journey complete</div>
            <div className="text-red-500 text-[11px] mt-1">This ticket has been validated and cannot be reused.</div>
          </div>
        )}

        <button onClick={() => navigate("/majhe/tickets")} className="majhe-btn majhe-btn--ghost mt-4">
          Back to My Tickets
        </button>
      </div>
    </div>
  );
}

function DetailField({ label, value, icon }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-[#7B7390] font-semibold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-sm font-bold text-[#18142A] mt-0.5 break-words">{value}</div>
    </div>
  );
}

function Timeline({ milestones }) {
  return (
    <div className="relative pl-6">
      <div className="absolute left-[10px] top-2 bottom-2 w-[2px] bg-[#EDE5FF]"/>
      {milestones.map((m, i) => (
        <div key={i} className="relative pb-3 last:pb-0">
          <div className={`absolute -left-[19px] top-0 w-5 h-5 rounded-full flex items-center justify-center ${
            m.is_current ? "bg-[#5B2EE5] animate-pulse" : m.completed ? "bg-[#2D1170]" : "bg-[#EDE5FF]"
          }`}>
            {m.completed && !m.is_current && <CheckCircle size={12} weight="fill" className="text-white"/>}
            {m.is_current && <span className="w-2 h-2 rounded-full bg-white"/>}
          </div>
          <div className={`text-sm font-bold ${m.is_current ? "text-[#5B2EE5]" : m.completed ? "text-[#18142A]" : "text-[#94889F]"}`}>
            {m.text}
          </div>
          <div className="text-[10.5px] text-[#94889F]">
            {new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      ))}
    </div>
  );
}
